import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';

import { interpolate } from '../../../utils/globalHelpers';
import { getMainClientInfo } from '../../Main/selectors';
import { getMaskedValue } from './helpers';
import { initialState } from './reducer';

const selectPaymentSourcesDomain = (state) => state.paymentSources || initialState;

/**
 * Get the information on the currently selected account (the 'existing' account or the 'new'
 * account). Defaults to an empty object if somehow neither are selected.
 * @returns {object} account info
 */
const makeSelectChosenAccountInfo = (paymentType) => createSelector(
  makeSelectUseAccountInfoExistingByPaymentType(paymentType),
  makeSelectPaymentSources('useAccountInfoNew'),
  makeSelectAccountInfoExistingByPaymentType(paymentType),
  makeSelectPaymentSources('accountInfoNew'),
  (
    useAccountInfoExisting,
    useAccountInfoNew,
    accountInfoExisting,
    accountInfoNew,
  ) => {
    if (useAccountInfoExisting) {
      return accountInfoExisting;
    }
    if (useAccountInfoNew) {
      return accountInfoNew;
    }
    return {};
  },
);

/**
 * Get the account type (checkings vs savings) of the currently chosen account
 */
const makeSelectChosenAccountType = (paymentType) => createSelector(
  makeSelectChosenAccountInfo(paymentType),
  (accountInfo) => accountInfo.accountType,
);

/**
 * @description determines payment source banner error to render and formats if necessary
 */
const makeSelectFormattedBannerError = () => createSelector(
  makeSelectPaymentSources('bannerError'),
  makeSelectPaymentSources('validateRoutingError'),
  getMainClientInfo('customerServicePhoneNumber'),
  (bannerError, validateRoutingError, phoneNumber) => {
    const errorMessage = bannerError || validateRoutingError || '';
    return interpolate(errorMessage, { customerServicePhoneNumber: phoneNumber });
  },
);

/**
 * @description selects data utilized by OTP edit payment source drawer on desktop
 */
const makeSelectDesktopSourceDrawerProps = (paymentType) => createSelector(
  makeSelectFormattedBannerError(),
  makeSelectFormValues(paymentType),
  makeSelectIsExistingInfo(paymentType),
  makeSelectIsNewInfo(),
  makeSelectUseAccountInfoExistingByPaymentType(paymentType),
  makeSelectPaymentSources('useAccountInfoNew'),
  makeSelectPaymentSources('validateRoutingLoading'),
  (
    sourceDrawerBannerError,
    { formErrors, formValues },
    isExistingInfo,
    isNewInfo,
    useExistingInfo,
    useNewInfo,
    validateRoutingLoading,
  ) => ({
    formErrors,
    formValues,
    isExistingInfo,
    isNewInfo,
    sourceDrawerBannerError,
    useExistingInfo,
    useNewInfo,
    validateRoutingLoading,
  }),
);

const makeSelectFormValues = (paymentType) => createSelector(
  makeSelectAccountInfoExistingByPaymentType(paymentType),
  makeSelectPaymentSources('accountInfoNew'),
  makeSelectPaymentSources('formErrors'),
  makeSelectPaymentSources('formValues'),
  makeSelectIsExistingInfo(paymentType),
  makeSelectUseAccountInfoExistingByPaymentType(paymentType),
  makeSelectPaymentSources('useAccountInfoNew'),
  (
    accountInfoExisting,
    accountInfoNew,
    formErrors,
    formValues,
    isExistingInfo,
    useAccountInfoExisting,
    useAccountInfoNew,
  ) => {
    if (isExistingInfo && useAccountInfoExisting) {
      const {
        accountType,
        maskedAccountNumber,
        maskedNameOnAccount,
        maskedRoutingNumber,
      } = accountInfoExisting;

      return {
        formErrors: initialState.formErrors,
        formValues: {
          accountNumber: maskedAccountNumber,
          accountNumberConfirm: maskedAccountNumber,
          accountType,
          nameOnAccount: maskedNameOnAccount,
          routingNumber: maskedRoutingNumber,
        },
      };
    }

    if (useAccountInfoNew) {
      const {
        accountNumber,
        accountType,
        nameOnAccount,
        routingNumber,
      } = accountInfoNew;

      return {
        formErrors: initialState.formErrors,
        formValues: {
          accountNumber: getMaskedValue(accountNumber),
          accountNumberConfirm: getMaskedValue(accountNumber),
          accountType,
          nameOnAccount: getMaskedValue(nameOnAccount),
          routingNumber: getMaskedValue(routingNumber),
        },
      };
    }

    return { formErrors, formValues };
  },
);

const makeSelectIsExistingInfo = (paymentType) => createSelector(
  makeSelectAccountInfoExistingByPaymentType(paymentType),
  (accountInfoExisting) => !isEmpty(accountInfoExisting),
);

const makeSelectIsNewInfo = () => createSelector(
  makeSelectPaymentSources('accountInfoNew'),
  (accountInfoNew) => !isEmpty(accountInfoNew),
);

const makeSelectPayFromButtonProps = (paymentType) => createSelector(
  makeSelectAccountInfoExistingByPaymentType(paymentType),
  makeSelectPaymentSources('accountInfoNew'),
  makeSelectIsExistingInfo(paymentType),
  makeSelectUseAccountInfoExistingByPaymentType(paymentType),
  makeSelectPaymentSources('useAccountInfoNew'),
  (
    accountInfoExisting,
    accountInfoNew,
    isExistingInfo,
    useAccountInfoExisting,
    useAccountInfoNew,
  ) => {
    if (isExistingInfo && useAccountInfoExisting) {
      const { accountType, maskedAccountNumber } = accountInfoExisting;
      const accountTypeValue = accountType === 'Saving' ? 'Savings' : accountType;
      return {
        noPaymentSource: false,
        value: `${accountTypeValue} (...${maskedAccountNumber.slice(-4)})`,
      };
    }

    if (useAccountInfoNew) {
      const { accountNumber, accountType } = accountInfoNew;
      const accountTypeValue = accountType === 'Saving' ? 'Savings' : accountType;
      return {
        noPaymentSource: false,
        value: `${accountTypeValue} (...${accountNumber.slice(-4)})`,
      };
    }

    return { noPaymentSource: true, value: '' };
  },
);

const makeSelectAccountInfoExistingByPaymentType = (paymentType) => createSelector(
  makeSelectPaymentSources('accountInfoExisting'),
  (accountInfoExisting) => accountInfoExisting[paymentType],
);

const makeSelectUseAccountInfoExistingByPaymentType = (paymentType) => createSelector(
  makeSelectPaymentSources('useAccountInfoExisting'),
  (useAccountInfoExisting) => useAccountInfoExisting[paymentType],
);

const makeSelectPaymentSourcesNotifications = () => createSelector(
  makeSelectPaymentSources('validateRoutingError'),
  (validateRoutingError) => validateRoutingError ? [validateRoutingError] : [],
);

const makeSelectPaymentSources = (prop) => createSelector(
  selectPaymentSourcesDomain,
  (paymentSourcesState) => paymentSourcesState[prop],
);

export default makeSelectPaymentSources;
export {
  makeSelectAccountInfoExistingByPaymentType,
  makeSelectChosenAccountInfo,
  makeSelectChosenAccountType,
  makeSelectDesktopSourceDrawerProps,
  makeSelectFormValues,
  makeSelectIsExistingInfo,
  makeSelectIsNewInfo,
  makeSelectPayFromButtonProps,
  makeSelectPaymentSourcesNotifications,
  makeSelectUseAccountInfoExistingByPaymentType,
  selectPaymentSourcesDomain,
};
